import { useEffect, useState } from "react";
import { Check, Share, Smartphone, X } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const setHead = (tag, attrs) => {
  const el = document.createElement(tag);
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  document.head.appendChild(el);
  return el;
};

const useInstallHead = (token) => {
  const [prompt, setPrompt] = useState(null);
  useEffect(() => {
    const els = [
      setHead("link", { rel: "manifest", href: `${BASE}/api/loyalty/manifest/${token}.webmanifest` }),
      setHead("link", { rel: "apple-touch-icon", href: `${BASE}/api/loyalty/icon-180.png` }),
      setHead("meta", { name: "apple-mobile-web-app-capable", content: "yes" }),
      setHead("meta", { name: "mobile-web-app-capable", content: "yes" }),
      setHead("meta", { name: "apple-mobile-web-app-title", content: "BANY" }),
    ];
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/tessera-sw.js", { scope: "/tessera/" }).catch(() => {});
    return () => { els.forEach((el) => el.remove()); window.removeEventListener("beforeinstallprompt", onPrompt); };
  }, [token]);
  return [prompt, setPrompt];
};

const IosSteps = ({ onClose }) => (
  <div data-testid="save-phone-ios-steps" className="mt-4 rounded-2xl border border-[rgba(11,31,58,0.2)] bg-white p-5 text-sm text-left relative">
    <button data-testid="save-phone-ios-close" onClick={onClose} aria-label="Chiudi" className="absolute right-3 top-3 text-[#6F6466]"><X className="w-4 h-4" /></button>
    <p className="font-semibold mb-3">Su iPhone, da Safari:</p>
    <ol className="space-y-2 text-[#1A1416]">
      <li className="flex gap-2"><span className="font-mono-alt">1.</span><span>Tocca <Share className="inline w-4 h-4 text-[#0B1F3A] align-[-2px]" /> <strong>Condividi</strong> in basso</span></li>
      <li className="flex gap-2"><span className="font-mono-alt">2.</span><span>Scegli <strong>Aggiungi alla schermata Home</strong></span></li>
      <li className="flex gap-2"><span className="font-mono-alt">3.</span><span>Tocca <strong>Aggiungi</strong>: trovi l'icona BANY tra le app</span></li>
    </ol>
  </div>
);

const OtherSteps = () => (
  <p data-testid="save-phone-other-steps" className="mt-4 text-sm text-[#6F6466]">
    Apri il menu del browser (⋮) e scegli <strong>Aggiungi a schermata Home</strong> o <strong>Installa app</strong>.
  </p>
);

export const SaveToPhone = ({ token }) => {
  const [prompt, setPrompt] = useInstallHead(token);
  const [help, setHelp] = useState(false);
  const [saved, setSaved] = useState(isStandalone());

  if (saved) {
    return <p data-testid="save-phone-done" className="mt-6 flex items-center justify-center gap-2 text-sm text-[#0B1F3A]"><Check className="w-4 h-4" /> Tessera salvata sul telefono</p>;
  }

  const save = async () => {
    if (!prompt) return setHelp(true);
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    setPrompt(null);
    if (outcome === "accepted") setSaved(true);
  };

  return (
    <div className="mt-6 text-center">
      <button data-testid="save-phone-button" onClick={save}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0B1F3A] text-[#F7F2EC] px-6 py-4 text-sm font-bold uppercase tracking-[0.12em] hover:bg-[#071528] hover:-translate-y-0.5 transition-[transform,background-color] duration-300">
        <Smartphone className="w-4 h-4" /> Salva sul telefono
      </button>
      {help && (isIOS() ? <IosSteps onClose={() => setHelp(false)} /> : <OtherSteps />)}
    </div>
  );
};
